// Journal articles — Planet Air Adventure
// Used by the React pages AND scripts/prerender.mjs. Keep this file free of imports.
// To add an article: copy one object, give it a new unique slug, and fill it in.
// Every article opens with `answer` — a direct 2–3 sentence answer to the title's
// question. Google's AI Overviews, ChatGPT and Perplexity quote these.

const IMG = {
  monastery:
    "https://images.unsplash.com/photo-1729180253317-93ba9cf4ceb4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  festival:
    "https://images.unsplash.com/photo-1667984849405-9d779b049d74?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  trek: "https://images.unsplash.com/photo-1645033393602-4f7623917853?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  flags:
    "https://images.unsplash.com/photo-1571401835393-8c5f35328320?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const JOURNAL_PATH = "/journal";
export const JOURNAL_AUTHOR = "Planet Air Adventure curators, Paro";

export const ARTICLES = [
  {
    slug: "best-time-to-visit-bhutan",
    tag: "Field Notes",
    read: "7 min read",
    published: "2026-11-03",
    updated: "2026-11-03",
    title: "When to visit Bhutan — a month-by-month field guide",
    seoTitle: "Best Time to Visit Bhutan: Month-by-Month Guide",
    description:
      "The best time to visit Bhutan is spring (March–May) and autumn (September–November). A month-by-month guide to weather, festivals, trekking and crowds from our team in Paro.",
    excerpt:
      "Clear skies in October, rhododendrons in April, festivals in autumn. The right month decides half of your journey.",
    img: IMG.monastery,
    imgAlt: "Taktsang (Tiger's Nest) Monastery on a cliff above the Paro valley",
    answer:
      "The best time to visit Bhutan is spring (March to May) and autumn (September to November). Autumn brings the clearest Himalayan views and the big Thimphu Tsechu festival; spring brings rhododendrons in bloom and the Paro Tsechu. Winter is cold but bright and quiet, and the summer monsoon (June to August) is green, wet and the least crowded.",
    sections: [
      {
        h: "Bhutan's seasons at a glance",
        list: [
          "Spring (March–May): mild days, flowering rhododendrons, Paro Tsechu, good trekking.",
          "Summer (June–August): monsoon rain, lush valleys, few visitors, Haa Summer Festival in July.",
          "Autumn (September–November): crisp air, the clearest mountain views, Thimphu Tsechu, peak trekking.",
          "Winter (December–February): cold, dry and sunny, black-necked cranes in Phobjikha, quiet monasteries.",
        ],
      },
      {
        h: "Spring: March to May",
        paras: [
          "Spring is many travellers' favourite season. The hillsides around Dochula Pass and the trails above Paro turn red and pink with rhododendrons, and the valleys warm up quickly after winter.",
          "The Paro Tsechu, one of Bhutan's largest masked-dance festivals, usually falls in March or April. Festival dates follow the Bhutanese lunar calendar and change every year, so hotels in Paro fill early around it.",
          "Haze can soften distant mountain views later in spring, but this is a very good time for treks such as the Druk Path.",
        ],
      },
      {
        h: "Summer: June to August",
        paras: [
          "Summer is Bhutan's monsoon. Expect afternoon showers, cloud on the high peaks and the occasional landslide delay on mountain roads. In return the valleys are intensely green, rice terraces are full, and you share famous sites with very few other visitors.",
          "The Haa Summer Festival in July is a relaxed celebration of nomadic life, food and games in the remote Haa valley.",
        ],
      },
      {
        h: "Autumn: September to November",
        paras: [
          "Once the monsoon clears in late September, Bhutan has its most reliable weather of the year: blue skies, cool nights and long views of the Himalaya from passes like Dochula.",
          "The Thimphu Tsechu, held in September or October, is the country's best-known festival. Autumn is also the main season for high-altitude treks. It is the busiest time of year, so book flights into Paro and festival-period hotels well ahead.",
        ],
      },
      {
        h: "Winter: December to February",
        paras: [
          "Winter days are often clear and sunny, with cold mornings and evenings and snow on the high passes. It is a peaceful time to visit temples and dzongs without crowds.",
          "From late autumn through winter, rare black-necked cranes roost in the Phobjikha valley around Gangtey. Lower valleys such as Punakha stay mild, which makes them a good base in the colder months.",
        ],
      },
      {
        h: "Best time for trekking and festivals",
        paras: [
          "For trekking, aim for March to May or late September to November, when trails are drier and passes are open. The highest treks are best in autumn.",
          "For festivals, check the exact Tsechu dates for your year before you book, then build your itinerary around them. We can tell you the confirmed dates and plan around them.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the cheapest time to visit Bhutan?",
        a: "The daily Sustainable Development Fee is the same all year, but hotels and flights are often easier to book and sometimes cheaper in the summer monsoon (June to August) and in winter (December to February).",
      },
      {
        q: "Is it worth visiting Bhutan in the monsoon?",
        a: "Yes, if you don't mind some rain. Valleys are at their greenest and sites are quiet. Mountain views are less reliable and some roads can be slowed by landslides, so keep the itinerary flexible.",
      },
      {
        q: "When are the Paro and Thimphu Tsechu festivals?",
        a: "Paro Tsechu usually falls in March or April, and Thimphu Tsechu in September or October. Exact dates follow the lunar calendar and change every year.",
      },
    ],
    related: ["cultural-circuit", "tsechu-festival-journey", "druk-path-trek"],
  },
  {
    slug: "bhutan-sustainable-development-fee",
    tag: "How to",
    read: "5 min read",
    published: "2026-11-03",
    updated: "2026-11-03",
    title: "The Sustainable Development Fee — what it costs and what it funds",
    seoTitle: "Bhutan SDF 2026: Sustainable Development Fee Explained",
    description:
      "Bhutan's Sustainable Development Fee (SDF) is USD 100 per adult per night for most visitors, ₹1,200 for Indian nationals. 2026 rates for adults and children, and what the fee pays for.",
    excerpt:
      "Bhutan charges a daily SDF. Here is what it costs, where it goes, and why it makes your trip more meaningful, not more expensive.",
    img: IMG.festival,
    imgAlt: "Masked dancers in bright costumes at a Bhutanese Tsechu festival",
    answer:
      "Bhutan's Sustainable Development Fee (SDF) is a daily levy paid by visitors for every night they stay. Most international adults pay USD 100 per night, a reduced rate valid until 31 August 2027; children aged 6–12 pay half and children under 6 pay nothing. Indian nationals pay ₹1,200 per night.",
    sections: [
      {
        h: "SDF rates in 2026",
        list: [
          "International adults: USD 100 per person per night (reduced rate, valid until 31 August 2027).",
          "International children aged 6–12: USD 50 per night.",
          "Children under 6: exempt.",
          "Indian nationals: ₹1,200 per adult per night; children aged 6–12 pay ₹600; under-6s are exempt.",
          "Bangladeshi nationals: USD 15 per person per night.",
        ],
      },
      {
        h: "SDF for Indian tourists: ₹1,200 per night",
        paras: [
          "Indian nationals pay a much lower SDF than other international visitors: ₹1,200 per adult per night, paid in Indian rupees. Children aged 6–12 pay ₹600 per night and children under 6 pay nothing. Indian travellers also need no visa and pay no visa fee, so the SDF is the only government fee on the trip.",
          "For example, a family of two adults and one 8-year-old staying 5 nights pays (2 × ₹1,200 + ₹600) × 5 = ₹15,000 in SDF. When you book with Planet Air Adventure, we include it in your quote and pay it on your behalf.",
        ],
      },
      {
        h: "What the SDF pays for",
        paras: [
          "The SDF is how Bhutan funds its 'high value, low volume' approach to tourism. The money goes to the government, not to your tour operator, and supports free healthcare and education for Bhutanese citizens, forest conservation, carbon-neutral development and the upkeep of the country's heritage.",
          "It also keeps visitor numbers sustainable. Fewer, longer, better-planned visits mean quieter monasteries, cleaner trails and a tourism economy that benefits local communities.",
        ],
      },
      {
        h: "How the SDF is paid",
        paras: [
          "The SDF is paid in advance, together with your visa application, for each night you will spend in Bhutan. When you travel with a licensed tour operator such as Planet Air Adventure, it is included in your quote and paid on your behalf.",
          "It is charged per night, not per day, so a seven-night trip carries seven nights of SDF. If your trip is cancelled before arrival, the SDF can generally be refunded under the rules in force at the time; ask us for the current conditions before you book.",
        ],
      },
      {
        h: "Is the SDF the same as the visa fee?",
        paras: [
          "No. The visa fee is a separate one-time charge of USD 40 per person for international visitors. Indian nationals don't need a visa and pay no visa fee, but they do pay the ₹1,200 SDF.",
        ],
      },
      {
        h: "Example: a 7-night trip for two adults",
        paras: [
          "For two international adults staying seven nights, the SDF is 2 × 7 × USD 100 = USD 1,400, plus USD 80 in visa fees. For two Indian adults it is 2 × 7 × ₹1,200 = ₹16,800, with no visa fee. Hotels, guide, transport and meals are priced separately and included in our packages.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is the Bhutan SDF included in tour packages?",
        a: "With Planet Air Adventure, yes. Every quote includes the SDF and, for international visitors, the visa fee, so there are no surprises.",
      },
      {
        q: "How much is the SDF for Indian tourists in Bhutan?",
        a: "Indian nationals pay ₹1,200 per adult per night. Children aged 6–12 pay ₹600 per night, children under 6 are exempt, and there is no visa fee for Indians.",
      },
      {
        q: "Do children pay the SDF in Bhutan?",
        a: "Children under 6 pay nothing. Children aged 6 to 12 pay half: USD 50 per night for international visitors, or ₹600 per night for Indian nationals.",
      },
      {
        q: "Will the SDF go back up to USD 200?",
        a: "The reduced USD 100 rate is set to run until 31 August 2027. What happens after that is up to the Government of Bhutan, so check the current rate when you plan a trip beyond that date.",
      },
    ],
    related: ["cultural-circuit", "glimpse-of-bhutan"],
    links: [{ href: "/bhutan-tour-packages-from-india", label: "Bhutan tour packages from India" }],
  },
  {
    slug: "bhutan-visa-requirements",
    tag: "How to",
    read: "6 min read",
    published: "2026-11-03",
    updated: "2026-11-03",
    title: "Bhutan visa requirements — how to get your visa",
    seoTitle: "Bhutan Visa Requirements 2026: How to Apply",
    description:
      "How to get a Bhutan tourist visa in 2026: the USD 40 fee, documents you need, processing time, insurance and the separate entry permit for Indian nationals.",
    excerpt:
      "Who needs a visa, which documents to prepare, how long it takes, and how your tour operator handles it.",
    img: IMG.flags,
    imgAlt: "Prayer flags above a valley in Bhutan",
    answer:
      "Almost every visitor to Bhutan needs a visa, applied for online before travel through Bhutan's Department of Immigration or through a licensed tour operator. The visa fee is a one-time USD 40 per person, and the Sustainable Development Fee is paid with the application. Indian nationals don't need a visa; they receive a free entry permit instead.",
    sections: [
      {
        h: "Who needs a visa for Bhutan?",
        paras: [
          "All international visitors need a visa, with the main exception of Indian nationals, who travel on an entry permit. Bangladeshi and Maldivian nationals also follow their own arrangements, so check the rules for your passport before you plan.",
        ],
      },
      {
        h: "What you need to apply",
        list: [
          "A digital copy of your passport, valid for at least six months beyond your departure from Bhutan.",
          "A recent digital passport-style photo.",
          "Your arrival and departure dates.",
          "Payment of the USD 40 visa fee (one-time, non-refundable).",
          "Payment of the Sustainable Development Fee for each night of your stay.",
        ],
      },
      {
        h: "How the process works",
        paras: [
          "You can apply yourself on the Department of Immigration's website, or let your tour operator apply for you. When you book with Planet Air Adventure, we prepare and submit the application once your itinerary is confirmed.",
          "Processing usually takes up to five working days. You receive a visa clearance letter by email; keep it with you, because airlines may ask for it at check-in, and the visa itself is stamped in your passport when you arrive in Bhutan.",
        ],
      },
      {
        h: "Do you need travel insurance?",
        paras: [
          "Travel insurance is no longer a visa requirement: Bhutan lifted the mandatory insurance rule in April 2024. It is still strongly recommended, especially for treks and remote areas, with cover for medical treatment and evacuation.",
        ],
      },
      {
        h: "Indian nationals: the entry permit",
        paras: [
          "Indian travellers don't need a visa. Adults need a valid Indian passport with at least six months' validity or an original Voter ID card; children can use a passport or a birth certificate. The entry permit covers Thimphu and Paro, and a route permit is needed for other districts such as Punakha. We arrange both. See our guide for Indian travellers for more detail.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much is a Bhutan visa?",
        a: "The Bhutan tourist visa fee is USD 40 per person, paid once per trip and non-refundable. The daily Sustainable Development Fee is charged separately.",
      },
      {
        q: "How long does a Bhutan visa take?",
        a: "Usually up to five working days once a complete application and payment are submitted. Apply well before your flight.",
      },
      {
        q: "Can I get a Bhutan visa on arrival?",
        a: "Not as an international tourist. The visa must be approved before you travel; it is then stamped in your passport on arrival using your visa clearance letter.",
      },
      {
        q: "Do Indians need a visa for Bhutan?",
        a: "No. Indian nationals receive an entry permit instead, using a passport or Voter ID card.",
      },
    ],
    related: ["glimpse-of-bhutan", "cultural-circuit"],
    links: [{ href: "/bhutan-tour-packages-from-india", label: "Bhutan tour packages from India" }],
  },
  {
    slug: "how-to-get-to-bhutan",
    tag: "How to",
    read: "6 min read",
    published: "2026-11-03",
    updated: "2026-11-03",
    title: "How to get to Bhutan — flights to Paro and the road from India",
    seoTitle: "How to Get to Bhutan: Flights to Paro & Road Routes",
    description:
      "How to reach Bhutan: Drukair and Bhutan Airlines flights to Paro from Delhi, Kolkata, Bangkok, Singapore, Kathmandu and more, or overland from India via Phuentsholing.",
    excerpt:
      "One international airport, two airlines and a famous landing — plus the overland route from India.",
    img: IMG.monastery,
    imgAlt: "Paro valley in Bhutan seen from the trail to Tiger's Nest",
    answer:
      "Most visitors reach Bhutan by flying into Paro (PBH), the country's only international airport, on Drukair or Bhutan Airlines from cities including Delhi, Kolkata, Bagdogra, Guwahati, Bangkok, Singapore, Kathmandu and Dhaka. Travellers from India can also enter by road at Phuentsholing, across the border from Jaigaon in West Bengal.",
    sections: [
      {
        h: "Flying into Paro",
        paras: [
          "Paro International Airport sits in a narrow valley surrounded by high ridges, and its approach is one of the most memorable in the world. Only specially trained pilots fly it, in daylight and good visibility.",
          "Two airlines serve Paro: Drukair (Royal Bhutan Airlines) and Bhutan Airlines. Routes connect Paro with Delhi, Kolkata, Bagdogra and Guwahati in India, and with Bangkok, Singapore, Kathmandu and Dhaka. Schedules are limited and seats sell out in peak seasons, so book early.",
          "International visitors should have their visa clearance before ticketing or check-in. We book Drukair and Bhutan Airlines tickets together with your tour so dates, visa and pick-up line up.",
        ],
      },
      {
        h: "Tips for the flight",
        list: [
          "On clear days, flights between Kathmandu and Paro pass close to the high Himalaya, including Everest and Kanchenjunga. Ask for a window seat.",
          "Weather can delay flights in and out of Paro, so avoid tight same-day international connections on your way home.",
          "Fly to a regional hub such as Bangkok, Delhi, Kolkata or Singapore first, then connect to Paro.",
        ],
      },
      {
        h: "By road from India via Phuentsholing",
        paras: [
          "Phuentsholing, across the border from Jaigaon in West Bengal, is the main land crossing. The nearest Indian airport is Bagdogra, and the nearest railway stations are Hasimara and New Jalpaiguri (NJP).",
          "From Phuentsholing it is a winding mountain drive to Thimphu, usually most of a day. Many Indian guests fly in to Paro and drive out via Phuentsholing, or the other way round. International visitors can also use this crossing with a valid Bhutan visa clearance and the right Indian visa for the India leg.",
        ],
      },
      {
        h: "Arriving: what happens next",
        paras: [
          "At Paro, your visa is stamped on arrival using your clearance letter (Indian nationals receive their entry permit). Your guide and driver meet you outside arrivals. Thimphu is about an hour's drive from the airport.",
        ],
      },
    ],
    faqs: [
      {
        q: "Which airlines fly to Bhutan?",
        a: "Drukair (Royal Bhutan Airlines) and Bhutan Airlines are the two airlines flying into Paro, Bhutan's only international airport.",
      },
      {
        q: "Can you fly to Bhutan from India?",
        a: "Yes. Flights to Paro operate from Delhi, Kolkata, Bagdogra and Guwahati. Indian travellers can also enter by road at Phuentsholing.",
      },
      {
        q: "How far is Thimphu from Paro airport?",
        a: "About an hour's drive along the Pachu and Wangchu rivers.",
      },
    ],
    related: ["cultural-circuit", "glimpse-of-bhutan"],
    links: [{ href: "/bhutan-tour-packages-from-india", label: "Bhutan tour packages from India" }],
  },
  {
    slug: "druk-path-trek-packing-list",
    tag: "Trail",
    read: "7 min read",
    published: "2026-11-03",
    updated: "2026-11-03",
    title: "Preparing for the Druk Path — an honest packing list",
    seoTitle: "Druk Path Trek Packing List & Preparation Guide",
    description:
      "What to pack for the Druk Path Trek in Bhutan: clothing layers for 4,000m+ passes, footwear, sleeping bag and the small things that matter, from our team in Paro.",
    excerpt:
      "Several days above the treeline between Paro and Thimphu. Layers, altitude, sleep and the small comforts that decide how well you walk.",
    img: IMG.trek,
    imgAlt: "Trekker on a high mountain trail in the Himalaya",
    answer:
      "For the Druk Path Trek, pack for cold, changeable mountain weather: warm layers including a down jacket, a waterproof shell, broken-in trekking boots, a sleeping bag suited to sub-zero nights, sun protection and a headlamp. The trail between Paro and Thimphu crosses passes above 4,000 metres, so nights are cold even in the main seasons.",
    sections: [
      {
        h: "About the Druk Path",
        paras: [
          "The Druk Path is one of Bhutan's classic treks, linking Paro and Thimphu over high ridges, past remote monasteries and alpine lakes. It reaches around 4,200 metres at its highest point. The best seasons are March to May and late September to November.",
          "On our treks a camp crew and pack animals carry tents, kitchen and your main bag, so you walk with a daypack only.",
        ],
      },
      {
        h: "Clothing",
        list: [
          "Base layers: two sets of moisture-wicking tops and leggings (merino or synthetic, not cotton).",
          "Mid layer: a fleece or light insulated jacket.",
          "Down jacket for evenings and early starts at high camps.",
          "Waterproof, breathable jacket and overtrousers.",
          "Trekking trousers, plus warm hat, sun hat, buff and warm gloves.",
          "Trekking socks (several pairs) and something warm and dry to wear in camp.",
        ],
      },
      {
        h: "Footwear",
        list: [
          "Waterproof trekking boots with ankle support, already broken in before the trip.",
          "Light camp shoes or sandals.",
          "Gaiters are useful in wet or snowy conditions.",
        ],
      },
      {
        h: "Sleeping",
        list: [
          "A sleeping bag comfortable below freezing; nights at the highest camps can drop well below zero.",
          "A sleeping-bag liner adds warmth and keeps the bag clean.",
          "Earplugs and an eye mask help on the first nights.",
        ],
      },
      {
        h: "Daypack essentials",
        list: [
          "A 25–35 litre daypack with rain cover.",
          "Water bottles or a hydration bladder (2 litres), plus purification tablets or a filter.",
          "High-SPF sunscreen, lip balm with SPF and sunglasses: the sun is strong at altitude.",
          "Headlamp with spare batteries.",
          "Trekking poles, which help a lot on the descents.",
          "Snacks, a small first-aid kit, blister plasters and any personal medication.",
        ],
      },
      {
        h: "Altitude and fitness",
        paras: [
          "You don't need mountaineering experience, but you should be comfortable walking several hours a day on uneven ground. Spend a few days in Paro or Thimphu before the trek, hiking to Tiger's Nest is a good warm-up, and walk at a steady pace. Talk to your doctor before travel about altitude and any medication.",
        ],
      },
    ],
    faqs: [
      {
        q: "How hard is the Druk Path Trek?",
        a: "It is a moderate trek. Days involve several hours of walking with climbs to passes above 4,000 metres, so good general fitness and a few days to acclimatise beforehand make a big difference.",
      },
      {
        q: "When is the best time to do the Druk Path?",
        a: "March to May and late September to November, when trails are drier and views clearer. Autumn usually has the most stable weather.",
      },
      {
        q: "Do I need to carry my own tent and food?",
        a: "Not on our treks. A camp crew and pack animals carry the tents, kitchen and your main bag; you carry only a daypack.",
      },
    ],
    related: ["druk-path-trek", "jomolhari-base-camp"],
  },
];
